import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Save } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { DatePicker } from '../ui/DatePicker';
import { SearchableSelect } from '../ui/SearchableSelect';
import { Button } from '../ui/Button';
import { useRBAC } from '../../contexts/RBACContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { mockData } from '../../services/mockData';
import { ticketsStore } from '../../services/ticketsStore';
import { ticketChannels, ticketPeople, ticketScopes, ticketStatuses } from '../../data/ticketOptions';
import { Ticket, TicketChannel, TicketDraft, TicketPerson, TicketScope, TicketStatus } from '../../types/ticket';
import { canEditIssuerFields, canEditTicket, getPersonName } from '../../utils/ticketPermissions';
interface TicketFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket?: Ticket | null;
}
interface TicketFormState {
  recipient: TicketPerson | '';
  cc: TicketPerson[];
  scope: TicketScope;
  client: string;
  description: string;
  channel: TicketChannel | '';
  deadline: string;
  status: TicketStatus;
  notes: string;
}
type FormErrors = Partial<Record<keyof TicketFormState, string>>;
const emptyForm: TicketFormState = {
  recipient: '',
  cc: [],
  scope: 'Interno',
  client: '',
  description: '',
  channel: '',
  deadline: '',
  status: 'Aperto',
  notes: ''
};
const ISSUER_ONLY_HINT = 'Modificabile solo da chi ha emesso il ticket';
export function TicketFormModal({
  isOpen,
  onClose,
  ticket
}: TicketFormModalProps) {
  const {
    currentUser
  } = useRBAC();
  const {
    addNotification
  } = useNotifications();
  const me = getPersonName(currentUser.name);
  const isEdit = Boolean(ticket);
  const [form, setForm] = useState<TicketFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const companyOptions = useMemo(() => mockData.getClients().map(c => ({
    value: c.name,
    label: c.name
  })), []);
  const canEdit = !ticket || canEditTicket(ticket, me);
  const issuerFieldsEditable = !ticket || canEditIssuerFields(ticket, me);
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setSaveSuccess(false);
    if (ticket) {
      setForm({
        recipient: ticket.recipient,
        cc: ticket.cc,
        scope: ticket.scope,
        client: ticket.client,
        description: ticket.description,
        channel: ticket.channel,
        deadline: ticket.deadline,
        status: ticket.status,
        notes: ticket.notes
      });
    } else {
      setForm(emptyForm);
    }
  }, [isOpen, ticket]);
  const update = <K extends keyof TicketFormState,>(key: K, value: TicketFormState[K]) => {
    setForm(prev => ({
      ...prev,
      [key]: value
    }));
    if (errors[key]) setErrors(prev => ({
      ...prev,
      [key]: undefined
    }));
  };
  const toggleCc = (person: TicketPerson) => {
    update('cc', form.cc.includes(person) ? form.cc.filter(p => p !== person) : [...form.cc, person]);
  };
  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!form.recipient) next.recipient = 'Seleziona il destinatario';
    if (form.scope === 'Azienda' && !form.client) next.client = "Seleziona l'azienda";
    if (!form.description.trim()) next.description = 'Inserisci la descrizione';
    if (!form.channel) next.channel = 'Seleziona il canale';
    if (!form.status) next.status = 'Seleziona lo stato';
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit || !validate()) return;
    const draft: TicketDraft = {
      createdBy: ticket ? ticket.createdBy : me,
      recipient: form.recipient as TicketPerson,
      cc: form.cc,
      scope: form.scope,
      client: form.scope === 'Azienda' ? form.client : '',
      description: form.description.trim(),
      channel: form.channel as TicketChannel,
      deadline: form.deadline,
      status: form.status,
      notes: form.notes
    };
    if (ticket) {
      ticketsStore.update(ticket.id, draft);
    } else {
      ticketsStore.add(draft);
    }
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      onClose();
      addNotification('success', isEdit ? 'Ticket modificato' : 'Ticket creato', isEdit ? 'Le modifiche al ticket sono state salvate.' : `Il ticket è stato inviato a ${draft.recipient}.`);
    }, 1500);
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Modifica ticket' : 'Nuovo ticket'} size="lg" data-id="element-1003" data-mp="2blcjp90mx0v5">
      <AnimatePresence mode="wait" data-id="element-1004" data-mp="1e3cfnjxw0ywr">
        {saveSuccess ? <motion.div key="success" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="flex flex-col items-center justify-center py-16" data-id="element-1005" data-mp="1j2p3a6wl2a0e">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-1006" data-mp="11l01869eitzq">
              <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-1007" data-mp="wmyhdj45iqn7" />
            </div>
            <p className="text-lg font-semibold text-slate-800" data-id="element-1008" data-mp="smofahjhckdm">
              {isEdit ? 'Ticket modificato con successo' : 'Ticket creato con successo'}
            </p>
          </motion.div> : <motion.form key="form" initial={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onSubmit={handleSubmit} noValidate className="space-y-6" data-id="element-1009" data-mp="itqrpwzms585">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1010" data-mp="4wkcgce48ono">
              <Select label="Destinatario *" value={form.recipient} onChange={e => update('recipient', e.target.value as TicketPerson | '')} options={[{
            value: '',
            label: 'Seleziona...'
          }, ...ticketPeople.map(p => ({
            value: p,
            label: p
          }))]} error={errors.recipient} disabled={!canEdit} data-id="element-1011" data-mp="4yewlohkccqp" />

              <fieldset data-id="element-1012" data-mp="e1akchqhzize">
                <legend className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1013" data-mp="i0ro96qa082g">
                  Per conoscenza
                </legend>
                <div className="flex flex-wrap gap-x-5 gap-y-2 pt-1" data-id="element-1014" data-mp="48bgjsmfjlv7">
                  {ticketPeople.map(person => <label key={person} className="flex items-center gap-2 cursor-pointer whitespace-nowrap" data-id="element-1015" data-mp="96muaefuigip">
                      <input type="checkbox" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={form.cc.includes(person)} onChange={() => toggleCc(person)} disabled={!canEdit} data-id="element-1016" data-mp="11pk1358utgjo" />
                      <span className="text-sm text-slate-700" data-id="element-1017" data-mp="1fukc4wncjr6z">{person}</span>
                    </label>)}
                </div>
              </fieldset>

              <fieldset className="col-span-full" data-id="element-1018" data-mp="s6sowy09ptqx">
                <legend className="sr-only" data-id="element-1019" data-mp="o3wdpsd24zx0">Azienda o Interno</legend>
                <div className="flex items-center gap-6" role="radiogroup" data-id="element-1020" data-mp="122w1yywnrcsn">
                  {ticketScopes.map(scope => <label key={scope} className={`flex items-center gap-2 ${issuerFieldsEditable && canEdit ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`} data-id="element-1021" data-mp="x3jdk9q51xny">
                      <input type="radio" name="ticket-scope" value={scope} className="border-slate-300 text-seform-orange focus:ring-seform-orange" checked={form.scope === scope} onChange={() => {
                  update('scope', scope);
                  if (scope === 'Interno') update('client', '');
                }} disabled={!issuerFieldsEditable || !canEdit} data-id="element-1022" data-mp="28ig982gtvl4a" />
                      <span className="text-sm text-slate-700" data-id="element-1023" data-mp="ui1vp9ed7j2k">{scope}</span>
                    </label>)}
                </div>
                {!issuerFieldsEditable && <p className="text-xs text-slate-500 mt-1.5" data-id="element-1024" data-mp="13j04e4zr0erh">
                    {ISSUER_ONLY_HINT}
                  </p>}
              </fieldset>

              {form.scope === 'Azienda' && <div className="col-span-full" data-id="element-1025" data-mp="35c7jpefd2wdd">
                  <SearchableSelect label="Azienda" value={form.client} onChange={e => update('client', e.target.value)} options={companyOptions} placeholder="Cerca e seleziona un'azienda..." error={errors.client} disabled={!canEdit} required data-id="element-1026" data-mp="2xrwxbb6a413y" />
                </div>}

              <div className="col-span-full" data-id="element-1027" data-mp="3gpzp1xq7oc5g">
                <Textarea label="Descrizione *" value={form.description} onChange={e => update('description', e.target.value)} placeholder="Descrivi la richiesta..." rows={4} error={errors.description} description={!issuerFieldsEditable ? ISSUER_ONLY_HINT : undefined} disabled={!issuerFieldsEditable || !canEdit} data-id="element-1028" data-mp="3br6as333oh48" />
              </div>

              <Select label="Canale *" value={form.channel} onChange={e => update('channel', e.target.value as TicketChannel | '')} options={[{
            value: '',
            label: 'Seleziona...'
          }, ...ticketChannels.map(c => ({
            value: c,
            label: c
          }))]} error={errors.channel} disabled={!canEdit} data-id="element-1029" data-mp="4zssmgfqczjd" />

              <DatePicker label="Data di scadenza" value={form.deadline} onChange={e => update('deadline', e.target.value)} description={!issuerFieldsEditable ? ISSUER_ONLY_HINT : undefined} disabled={!issuerFieldsEditable || !canEdit} data-id="element-1030" data-mp="jmug50afgmgf" />

              <Select label="Stato ticket *" value={form.status} onChange={e => update('status', e.target.value as TicketStatus)} options={ticketStatuses.map(s => ({
            value: s,
            label: s
          }))} error={errors.status} disabled={!canEdit} data-id="element-1031" data-mp="2ug704c3unocs" />

              <div className="col-span-full" data-id="element-1032" data-mp="1576djbtho5j1">
                <Textarea label="Note" value={form.notes} onChange={e => update('notes', e.target.value)} rows={2} disabled={!canEdit} data-id="element-1033" data-mp="155v9daw4svre" />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1034" data-mp="i7i9p64ujk8e">
              <Button type="button" variant="flat" onClick={onClose} data-id="element-1035" data-mp="m8296gcw6zxp">
                Annulla
              </Button>
              {canEdit && <Button type="submit" startContent={isEdit ? <Save className="h-4 w-4" data-id="element-1037" data-mp="2gltej3o6uxnc" /> : undefined} data-id="element-1036" data-mp="2cl9f1tg57hy1">
                  {isEdit ? 'Salva modifiche' : 'Crea Ticket'}
                </Button>}
            </div>
          </motion.form>}
      </AnimatePresence>
    </Modal>;
}